import {
  Avatar,
  Button,
  DropdownMenu,
  Icon,
  IconButton,
  RouterProvider,
  type IconName,
} from '@wandb/components';
import {Button as AriaButton} from 'react-aria-components';
import {useRef, useState} from 'react';
import {Link, useHref, useLocation, useNavigate} from 'react-router-dom';

import {
  AccountType,
  type Account,
  toShellAccount,
} from '../account/accountModel';
import {useAccountContext} from '../account/AccountProvider';
import {accountSwitchTarget} from '../account/accountSwitchTarget';
import {shellLogout} from '../account/logout';
import {isAuthenticationError} from '../identity/authError';
import {shellRouteClaimTable} from '../routeClaims';
import {useAdminTools, type AdminTools} from '../admin/useAdminTools';
import {AdminStateBanner} from './AdminStateBanner';
import {AppLauncher} from './AppLauncher';
import {AdminToolsSection} from './AdminToolsSection';
import {AriaNavHost} from './AriaNavHost';
import {CoreWeaveLogo} from './CoreWeaveLogo';
import {ForgeHeaderBreadcrumbs} from './ForgeHeaderBreadcrumbs';
import {HeaderLink} from './HeaderLink';
import {currentProductForPath} from './forgeProducts';
import {AnnouncementsButton, HelpResourcesMenu} from './HeaderResources';
import {
  accountSettingsBillingPath,
  accountSettingsMembersPath,
  accountSettingsSettingsPath,
  accountSettingsTeamsPath,
  accountSettingsApiKeysPath,
  automationsPath,
  profilePath,
  settingsApiKeysPath,
  subscriptionsPath,
  userSettingsPath,
} from './profileMenuPaths';
import css from './ShellHeader.module.css';
import {useShellHeaderActions} from './ShellHeaderContext';

const ABSOLUTE_URL = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Preserve the current Forge page through identity, except when already home.
 */
export const identityEntryHref = (
  path: '/login' | '/signup',
  location: {pathname: string; search: string; hash: string}
): string => {
  if (location.pathname === '/') {
    return path;
  }
  const next = `${location.pathname}${location.search}${location.hash}`;
  return `${path}?next=${encodeURIComponent(next)}`;
};

/**
 * react-router's `useHref` resolves every string against the current route, so
 * an external URL like `https://docs.wandb.ai` would come back as
 * `/wandb/home/https:/docs.wandb.ai`. Leave absolute URLs alone.
 */
const useShellHref = (to: string): string => {
  const routerHref = useHref(to);
  return ABSOLUTE_URL.test(to) ? to : routerHref;
};

export const ShellHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {viewer, selectedAccount, isLoading, error} = useAccountContext();
  const sessionUnavailable =
    viewer == null && error != null && !isAuthenticationError(error);
  const sessionPending = viewer == null && isLoading && error == null;
  const {onGlobalSearchPress} = useShellHeaderActions();
  const adminTools = useAdminTools(viewer);
  const shouldShowAdminBanner =
    adminTools.isAdminModeActive || adminTools.isImpersonating;
  const wandbRemote = window.__FORGE_SHELL_CONFIG__?.remotes?.wandb;
  const currentProduct = currentProductForPath(
    location.pathname,
    shellRouteClaimTable()
  );

  return (
    <RouterProvider navigate={navigate} useHref={useShellHref}>
      {shouldShowAdminBanner ? (
        <AdminStateBanner adminTools={adminTools} />
      ) : null}
      <header className={css.header}>
        <div className={css.left}>
          {viewer != null ? (
            <AppLauncher currentProduct={currentProduct} />
          ) : (
            sessionPending && (
              <div aria-hidden className={css.launcherPlaceholder} />
            )
          )}
          <Link aria-label="Forge home" className={css.brand} to="/">
            <CoreWeaveLogo />
            <span className={css.wordmark}>Forge</span>
          </Link>
          {currentProduct != null && (
            <ForgeHeaderBreadcrumbs product={currentProduct} />
          )}
        </div>
        <div className={css.right}>
          {viewer != null && (
            <>
              <div className={css.actions}>
                {onGlobalSearchPress != null && (
                  <IconButton
                    aria-label="Search"
                    icon="search"
                    size="md"
                    variant="ghost"
                    onPress={onGlobalSearchPress}
                  />
                )}
                <AnnouncementsButton />
                <HelpResourcesMenu />
                {wandbRemote ? (
                  <AriaNavHost
                    remote={wandbRemote}
                    selectedAccount={toShellAccount(isLoading, selectedAccount)}
                  />
                ) : null}
              </div>
              <AccountSelector adminTools={adminTools} />
            </>
          )}
          {viewer == null && (
            <>
              <HelpResourcesMenu />
              {sessionUnavailable || sessionPending ? null : (
                <>
                  <Button
                    href={identityEntryHref('/login', location)}
                    size="sm"
                    variant="ghost">
                    Log in
                  </Button>
                  <Button
                    href={identityEntryHref('/signup', location)}
                    endIcon="forward-next"
                    size="sm"
                    variant="primary">
                    Sign up
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </header>
    </RouterProvider>
  );
};

const accountDisplayName = (account: Account): string => {
  if (account.accountType === AccountType.Personal) {
    return account.username;
  }
  return account.name;
};

const accountDescription = (account: Account): string => {
  if (account.accountType === AccountType.Personal) {
    return 'Free';
  }
  if (account.memberCount < 0) {
    // Backend returns -1 when the viewer may not read org aggregates.
    return account.accountType;
  }
  const memberLabel = account.memberCount === 1 ? 'user' : 'users';
  return `${account.accountType} · ${account.memberCount} ${memberLabel}`;
};

const accountMenuName = (account: Account): string => {
  if (account.accountType === AccountType.Personal) {
    return 'Personal';
  }
  return account.name;
};

const accountIcon = (account: Account): IconName => {
  if (account.accountType === AccountType.Personal) {
    return 'user-profile-personal';
  }
  return 'organization-corporate';
};

const accountTriggerLabel = (
  selectedAccount: Account | null,
  isLoading: boolean,
  viewerUsername: string | null
): string => {
  if (selectedAccount != null) {
    return accountDisplayName(selectedAccount);
  }
  if (isLoading) {
    return 'Loading accounts…';
  }
  return viewerUsername ?? '';
};

type AccountMenuItemProps = {
  account: Account;
  onSelect: (account: Account) => void;
};

const AccountMenuItem = ({account, onSelect}: AccountMenuItemProps) => (
  <DropdownMenu.Item
    key={`${account.id}-${account.accountType}`}
    onAction={() => onSelect(account)}>
    <DropdownMenu.ItemDecorationLeft iconProps={{icon: accountIcon(account)}} />
    <DropdownMenu.ItemLabel>{accountMenuName(account)}</DropdownMenu.ItemLabel>
    <DropdownMenu.ItemDescription>
      {accountDescription(account)}
    </DropdownMenu.ItemDescription>
  </DropdownMenu.Item>
);

const AccountSelector = ({adminTools}: {adminTools: AdminTools}) => {
  const {
    viewer,
    accountList,
    selectedAccount,
    selectAccount,
    isLoading,
    isMock,
  } = useAccountContext();
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  if (viewer == null) {
    return null;
  }

  const routeClaims = shellRouteClaimTable();
  const otherAccounts = accountList.filter(
    account =>
      account.name !== selectedAccount?.name ||
      account.accountType !== selectedAccount?.accountType
  );

  let viewerProfilePath = '/';
  if (viewer.username != null) {
    viewerProfilePath = profilePath(viewer.username);
  }
  const profileLinks = [
    ['Profile', viewerProfilePath, 'users-team'],
    ['User settings', userSettingsPath(), 'settings-parameters'],
  ] as const;
  const accountLinks: Array<readonly [string, string, IconName]> = [];
  if (selectedAccount != null) {
    if (selectedAccount.accountType === AccountType.Personal) {
      accountLinks.push(
        ['API keys', settingsApiKeysPath(), 'key-admin'],
        ['Usage', subscriptionsPath(), 'chart-vertical-bars']
      );
    } else {
      const organizationName = selectedAccount.name;
      accountLinks.push(
        ['Automations', automationsPath(), 'job-automation'],
        ['Users', accountSettingsMembersPath(organizationName), 'user-author'],
        ['Teams', accountSettingsTeamsPath(organizationName), 'users-team'],
        [
          'Billing',
          accountSettingsBillingPath(organizationName),
          'credit-card-payment',
        ],
        ['Settings', accountSettingsSettingsPath(organizationName), 'settings'],
        ['API keys', accountSettingsApiKeysPath(organizationName), 'key-admin']
      );
    }
  }

  return (
    <>
      <AriaButton
        ref={triggerRef}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="Open account selector"
        className={css.userTrigger}
        onPress={() => setIsOpen(open => !open)}>
        <div className={css.userDetails}>
          <span className={css.userName}>{viewer.name}</span>
          <span className={css.organization}>
            {accountTriggerLabel(selectedAccount, isLoading, viewer.username)}
          </span>
        </div>
        <Avatar src={viewer.photoUrl ?? ''} alt={viewer.name} />
      </AriaButton>
      <DropdownMenu.Content
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        triggerRef={triggerRef}
        placement="bottom end"
        aria-label="Accounts">
        <DropdownMenu.Section>
          {viewer.email != null && (
            <DropdownMenu.Header>{viewer.email}</DropdownMenu.Header>
          )}
          {profileLinks.map(([label, path, icon]) => (
            <HeaderLink
              key={label}
              label={label}
              path={path}
              icon={icon}
              routeClaims={routeClaims}
              onAction={() => setIsOpen(false)}
            />
          ))}
        </DropdownMenu.Section>
        <AdminToolsSection
          viewer={viewer}
          adminTools={adminTools}
          routeClaims={routeClaims}
          pathname={location.pathname}
          onClose={() => setIsOpen(false)}
        />
        {selectedAccount != null && (
          <>
            <DropdownMenu.Separator />
            <DropdownMenu.Section>
              <DropdownMenu.Header>Account</DropdownMenu.Header>
              <DropdownMenu.Header>
                <div className={css.currentAccountRow}>
                  <Icon icon={accountIcon(selectedAccount)} />
                  <div className={css.currentAccountText}>
                    <span className={css.currentAccountName}>
                      {accountMenuName(selectedAccount)}
                    </span>
                    <span className={css.currentAccountDescription}>
                      {accountDescription(selectedAccount)}
                    </span>
                  </div>
                </div>
              </DropdownMenu.Header>
              {accountLinks.map(([label, path, icon]) => (
                <HeaderLink
                  key={label}
                  label={label}
                  path={path}
                  icon={icon}
                  routeClaims={routeClaims}
                  onAction={() => setIsOpen(false)}
                />
              ))}
            </DropdownMenu.Section>
          </>
        )}
        {otherAccounts.length > 0 && (
          <>
            <DropdownMenu.Separator />
            <DropdownMenu.Section>
              <DropdownMenu.Header>Other accounts</DropdownMenu.Header>
              {otherAccounts.map(account => (
                <AccountMenuItem
                  key={`${account.id}-${account.accountType}`}
                  account={account}
                  onSelect={account => {
                    selectAccount(account);
                    setIsOpen(false);
                    const to = accountSwitchTarget(
                      location.pathname,
                      routeClaims,
                      account
                    );
                    if (to != null) {
                      navigate(to);
                    }
                  }}
                />
              ))}
            </DropdownMenu.Section>
          </>
        )}
        <DropdownMenu.Separator />
        <DropdownMenu.Section>
          <DropdownMenu.Item
            key="Log out"
            onAction={async () => {
              await shellLogout(isMock);
            }}>
            <DropdownMenu.ItemDecorationLeft iconProps={{icon: 'log-out'}} />
            <DropdownMenu.ItemLabel>Log out</DropdownMenu.ItemLabel>
          </DropdownMenu.Item>
        </DropdownMenu.Section>
      </DropdownMenu.Content>
    </>
  );
};
